import React, { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Checkbox } from '../components/ui/checkbox.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart, Line, Legend, Area
} from 'recharts';
import { Plus, Save, Trash2, FileText, BarChart3, Building2, AlertCircle, Filter, TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const PIE_COLORS = ['#2563eb', '#0f172a', '#3b82f6', '#64748b', '#1e40af', '#94a3b8'];
const TIPOS_ACORDO = ['ACT', 'CCT', 'DCT', 'Outro'];
const ESCALAS_OPTS = ['5x2', '6x1', '12x36', '4x3', '5x1', '4x2', 'Outro'];
const fmt = (v) => typeof v === 'number' ? v.toLocaleString('pt-BR', { minimumFractionDigits: 0 }) : '-';

const emptyForm = () => ({
  categoria_sindical: '', tipo: 'CCT', vigencia_inicio: '2024-01-01', vigencia_fim: '2024-12-31',
  pisos_salariais: [{ valor: '', descricao: '' }],
  percentual_reajuste: '', horas_extras: [{ percentual: '', descricao: '' }],
  adicional_noturno: '', jornada_semanal: '44', jornada_mensal: '220',
  dias_folga_semana: '1', tipos_escala: ['5x2'],
  cesta_basica: false, cesta_basica_valor: 0,
  cartao: false, cartao_valor: 0,
  ano_referencia: 2024,
});

const Stat = ({ label, value, suffix = '', sub, color = 'slate' }) => (
  <div className={`rounded-lg p-4 ${color === 'blue' ? 'bg-blue-50' : color === 'green' ? 'bg-emerald-50' : color === 'amber' ? 'bg-amber-50' : 'bg-slate-50'}`} data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <p className="text-[11px] text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900">{value}{suffix}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const MultiFaixa = ({ count, pct, label }) => count > 0 ? (
  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs flex items-center gap-1.5" data-testid={`multi-faixa-${label}`}>
    <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
    <span className="text-amber-800"><strong>{count}</strong> acordos ({pct}%) com mais de uma faixa de <strong>{label}</strong></span>
  </div>
) : null;

// Category detail block (reusable for each categoria)
const CategoriaBlock = ({ cat }) => {
  if (!cat) return null;
  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-4" data-testid={`cat-block-${cat.categoria}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 text-sm">{cat.categoria}</h3>
        <div className="flex gap-2 text-xs">
          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{cat.n_empresas} empresas</span>
          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{cat.total_acordos} acordos</span>
        </div>
      </div>
      {/* Tipos */}
      <div className="flex gap-2 flex-wrap">
        {(cat.tipos || []).map((t, i) => (
          <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{t.tipo}: <strong>{t.count}</strong> ({t.pct}%)</span>
        ))}
      </div>
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Reajuste</p>
          <p className="text-sm font-bold">{cat.reajuste?.media?.toFixed(2)}%</p>
          <p className="text-[9px] text-slate-400">P50: {cat.reajuste?.p50?.toFixed(2)}%</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Piso Médio</p>
          <p className="text-sm font-bold">R$ {fmt(cat.pisos_salariais?.media)}</p>
          <p className="text-[9px] text-slate-400">P50: R$ {fmt(cat.pisos_salariais?.p50)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Jornada Sem.</p>
          <p className="text-sm font-bold">{cat.jornada_semanal?.media?.toFixed(0)}h</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Adic. Noturno</p>
          <p className="text-sm font-bold">{cat.adicional_noturno?.media?.toFixed(0)}%</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Hora Extra</p>
          <p className="text-sm font-bold">{cat.horas_extras?.media?.toFixed(0)}%</p>
          {cat.horas_extras?.empresas_multi_faixa > 0 && <p className="text-[9px] text-amber-600">{cat.horas_extras.empresas_multi_faixa} multi-faixa</p>}
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Folga/Semana</p>
          <p className="text-sm font-bold">{cat.dias_folga?.media?.toFixed(1)} dias</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Cesta Básica</p>
          <p className="text-sm font-bold">{cat.cesta_basica?.pct_sim}% sim</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500">Cartão</p>
          <p className="text-sm font-bold">{cat.cartao?.pct_sim}% sim</p>
        </div>
      </div>
      {/* Escalas */}
      {cat.escalas?.distribuicao?.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          <span className="text-[10px] text-slate-500 py-0.5">Escalas:</span>
          {cat.escalas.distribuicao.map((e, i) => (
            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{e.escala}: {e.count} ({e.pct}%)</span>
          ))}
        </div>
      )}
    </div>
  );
};

const AcordosColetivos = () => {
  const userRole = localStorage.getItem('userRole') || 'respondent';
  const [tab, setTab] = useState('tabulacao');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [myAcordos, setMyAcordos] = useState([]);
  const [tabulacao, setTabulacao] = useState(null);
  const [tabLoading, setTabLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [blockedN, setBlockedN] = useState(0);
  const [ano, setAno] = useState('2024');
  const [catFilter, setCatFilter] = useState('all');
  const [temporal, setTemporal] = useState(null);
  const [temporalLoading, setTemporalLoading] = useState(false);
  const [updatingInpc, setUpdatingInpc] = useState(false);

  const loadMyAcordos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/acordos-coletivos/me?ano=${ano}`, { headers: getHeaders() });
      setMyAcordos(data.acordos || []);
    } catch (e) { console.error(e); }
  }, [ano]);

  const loadTabulacao = useCallback(async () => {
    setTabLoading(true);
    setBlocked(false);
    try {
      let url = `${API}/acordos-coletivos/tabulacao?ano=${ano}`;
      if (catFilter && catFilter !== 'all') url += `&categoria=${encodeURIComponent(catFilter)}`;
      const { data } = await axios.get(url, { headers: getHeaders() });
      setTabulacao(data);
    } catch (e) {
      if (e.response?.status === 403) {
        setBlocked(true);
        const match = e.response.data?.detail?.match(/n=(\d+)/);
        setBlockedN(match ? parseInt(match[1]) : 0);
      }
      setTabulacao(null);
    }
    setTabLoading(false);
  }, [ano, catFilter]);

  useEffect(() => { loadMyAcordos(); loadTabulacao(); }, [loadMyAcordos, loadTabulacao]);

  const loadTemporal = useCallback(async () => {
    setTemporalLoading(true);
    try {
      const { data } = await axios.get(`${API}/acordos-coletivos/analise-temporal?ano=${ano}`, { headers: getHeaders() });
      setTemporal(data);
    } catch (e) {
      setTemporal(null);
    }
    setTemporalLoading(false);
  }, [ano]);

  useEffect(() => { loadTemporal(); }, [loadTemporal]);

  const handleUpdateInpc = async () => {
    setUpdatingInpc(true);
    try {
      const { data } = await axios.post(`${API}/inpc/atualizar`, {}, { headers: getHeaders() });
      toast.success(data.message);
      loadTemporal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar INPC');
    }
    setUpdatingInpc(false);
  };

  // Form handlers
  const onChange = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addPiso = () => setForm(p => ({ ...p, pisos_salariais: [...p.pisos_salariais, { valor: '', descricao: '' }] }));
  const updatePiso = (i, f, v) => setForm(p => ({ ...p, pisos_salariais: p.pisos_salariais.map((x, j) => j === i ? { ...x, [f]: v } : x) }));
  const removePiso = (i) => setForm(p => ({ ...p, pisos_salariais: p.pisos_salariais.filter((_, j) => j !== i) }));
  const addHE = () => setForm(p => ({ ...p, horas_extras: [...p.horas_extras, { percentual: '', descricao: '' }] }));
  const updateHE = (i, f, v) => setForm(p => ({ ...p, horas_extras: p.horas_extras.map((x, j) => j === i ? { ...x, [f]: v } : x) }));
  const removeHE = (i) => setForm(p => ({ ...p, horas_extras: p.horas_extras.filter((_, j) => j !== i) }));
  const toggleEscala = (e) => setForm(p => ({ ...p, tipos_escala: p.tipos_escala.includes(e) ? p.tipos_escala.filter(x => x !== e) : [...p.tipos_escala, e] }));

  const handleSubmit = async () => {
    if (!form.categoria_sindical.trim()) { toast.error('Informe a categoria sindical'); return; }
    try {
      const payload = {
        ...form,
        percentual_reajuste: parseFloat(form.percentual_reajuste) || 0,
        adicional_noturno: parseFloat(form.adicional_noturno) || 0,
        jornada_semanal: parseFloat(form.jornada_semanal) || 0,
        jornada_mensal: parseFloat(form.jornada_mensal) || 0,
        dias_folga_semana: parseFloat(form.dias_folga_semana) || 0,
        pisos_salariais: form.pisos_salariais.filter(p => p.valor).map(p => ({ valor: parseFloat(p.valor), descricao: p.descricao })),
        horas_extras: form.horas_extras.filter(h => h.percentual).map(h => ({ percentual: parseFloat(h.percentual), descricao: h.descricao })),
      };
      await axios.post(`${API}/acordos-coletivos`, payload, { headers: getHeaders() });
      toast.success('Acordo coletivo registrado!');
      setShowForm(false);
      setForm(emptyForm());
      loadMyAcordos();
      loadTabulacao();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/acordos-coletivos/${id}`, { headers: getHeaders() });
      toast.success('Acordo removido');
      loadMyAcordos();
      loadTabulacao();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  const t = tabulacao;

  return (
    <MainLayout title="Acordos Coletivos" userRole={userRole}>
      <div className="space-y-6" data-testid="acordos-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Select value={ano} onValueChange={v => { setAno(v); setCatFilter('all'); }}>
              <SelectTrigger className="w-[100px] h-9 text-sm" data-testid="ano-selector"><SelectValue /></SelectTrigger>
              <SelectContent>{[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            {t?.categorias_disponiveis?.length > 0 && (
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="categoria-filter">
                  <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                  <SelectValue placeholder="Filtrar categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas Categorias</SelectItem>
                  {t.categorias_disponiveis.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button onClick={() => { setForm(emptyForm()); setShowForm(true); }} className="gap-2" data-testid="new-acordo-btn">
            <Plus className="w-4 h-4" /> Novo Acordo
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto p-1">
            <TabsTrigger value="tabulacao" className="gap-1.5 text-sm" data-testid="tab-tabulacao">
              <BarChart3 className="w-3.5 h-3.5" /> Tabulacao do Mercado
            </TabsTrigger>
            <TabsTrigger value="temporal" className="gap-1.5 text-sm" data-testid="tab-temporal">
              <TrendingUp className="w-3.5 h-3.5" /> Analise Temporal
            </TabsTrigger>
            <TabsTrigger value="categorias" className="gap-1.5 text-sm" data-testid="tab-categorias">
              <Filter className="w-3.5 h-3.5" /> Por Categoria Sindical
            </TabsTrigger>
            <TabsTrigger value="meus" className="gap-1.5 text-sm" data-testid="tab-meus">
              <Building2 className="w-3.5 h-3.5" /> Meus Acordos ({myAcordos.length})
            </TabsTrigger>
          </TabsList>

          {/* ===== TABULACAO GERAL ===== */}
          <TabsContent value="tabulacao">
            {tabLoading ? <p className="text-center text-slate-400 py-10">Carregando...</p> :
              blocked ? (
                <div className="bg-slate-50 rounded-xl p-10 text-center" data-testid="blocked-overlay">
                  <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium">Governanca Estatistica</p>
                  <p className="text-xs text-slate-400 mt-1">Amostra insuficiente (n={blockedN}). Minimo: 5 empresas.</p>
                </div>
              ) : t && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat label="Empresas na Base" value={t.n} color="blue" sub="base estatistica" />
                    <Stat label="Acordos Registrados" value={t.total_acordos} />
                    <Stat label="Reajuste Medio" value={t.reajuste?.media?.toFixed(2)} suffix="%" color="green" sub={`P50: ${t.reajuste?.p50?.toFixed(2)}%`} />
                    <Stat label="Jornada Semanal" value={t.jornada_semanal?.media?.toFixed(0)} suffix="h" sub={`P50: ${t.jornada_semanal?.p50?.toFixed(0)}h`} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Tipo de Acordo</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={t.tipos || []} dataKey="count" nameKey="tipo" cx="50%" cy="50%" outerRadius={75} label={({ tipo, pct }) => `${tipo}: ${pct}%`}>
                              {(t.tipos || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v} acordos`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Categorias Sindicais</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={(t.categorias_sindicais || []).slice(0, 8)} layout="vertical" margin={{ left: 100 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis dataKey="categoria" type="category" tick={{ fontSize: 10 }} width={95} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#2563eb" radius={[0, 3, 3, 0]} name="Acordos" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-2">
                    <MultiFaixa count={t.pisos_salariais?.empresas_multi_faixa} pct={t.pisos_salariais?.pct_multi_faixa} label="piso salarial" />
                    <MultiFaixa count={t.horas_extras?.empresas_multi_faixa} pct={t.horas_extras?.pct_multi_faixa} label="horas extras" />
                    <MultiFaixa count={t.escalas?.empresas_multi_escala} pct={t.escalas?.pct_multi_escala} label="tipos de escala" />
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat label="Piso Salarial Medio" value={`R$ ${fmt(t.pisos_salariais?.media)}`} sub={`P50: R$ ${fmt(t.pisos_salariais?.p50)}`} />
                    <Stat label="Hora Extra Media" value={t.horas_extras?.media?.toFixed(0)} suffix="%" sub={`P50: ${t.horas_extras?.p50?.toFixed(0)}%`} />
                    <Stat label="Adicional Noturno" value={t.adicional_noturno?.media?.toFixed(0)} suffix="%" />
                    <Stat label="Dias Folga/Semana" value={t.dias_folga?.media?.toFixed(1)} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Escalas</CardTitle></CardHeader>
                      <CardContent>
                        {t.escalas?.distribuicao?.length > 0 ? (
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={t.escalas.distribuicao}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="escala" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#0f172a" radius={[3, 3, 0, 0]} name="Empresas" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : <p className="text-sm text-slate-400 text-center py-6">Sem dados</p>}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Cesta Basica e Cartao</CardTitle></CardHeader>
                      <CardContent className="space-y-5">
                        <div>
                          <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">Cesta Basica</span><span className="font-semibold">{t.cesta_basica?.pct_sim}% sim</span></div>
                          <div className="h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${t.cesta_basica?.pct_sim || 0}%` }} /></div>
                          {t.cesta_basica?.media_valor > 0 && (
                            <div className="mt-1.5 grid grid-cols-3 gap-2 text-[10px] text-slate-500">
                              <span>Media: <strong className="text-slate-800">R$ {fmt(t.cesta_basica.media_valor)}</strong></span>
                              <span>P25: R$ {fmt(t.cesta_basica.p25)}</span>
                              <span>P50: <strong className="text-blue-600">R$ {fmt(t.cesta_basica.p50)}</strong></span>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">Cartao</span><span className="font-semibold">{t.cartao?.pct_sim}% sim</span></div>
                          <div className="h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-slate-800 rounded-full" style={{ width: `${t.cartao?.pct_sim || 0}%` }} /></div>
                          {t.cartao?.media_valor > 0 && (
                            <div className="mt-1.5 grid grid-cols-3 gap-2 text-[10px] text-slate-500">
                              <span>Media: <strong className="text-slate-800">R$ {fmt(t.cartao.media_valor)}</strong></span>
                              <span>P25: R$ {fmt(t.cartao.p25)}</span>
                              <span>P50: <strong className="text-blue-600">R$ {fmt(t.cartao.p50)}</strong></span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Full stats table */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Resumo Estatistico</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="tabulacao-table">
                          <thead><tr className="border-b border-slate-200 text-left">
                            <th className="py-2 px-2 font-medium text-slate-600">Indicador</th>
                            <th className="py-2 px-2 text-right font-medium text-slate-600">Media</th>
                            <th className="py-2 px-2 text-right font-medium text-slate-600">P25</th>
                            <th className="py-2 px-2 text-right font-medium text-blue-600">P50</th>
                            <th className="py-2 px-2 text-right font-medium text-slate-600">P75</th>
                          </tr></thead>
                          <tbody>
                            {[
                              { label: 'Reajuste (%)', d: t.reajuste, s: '%' },
                              { label: 'Piso Salarial (R$)', d: t.pisos_salariais, s: '' },
                              { label: 'Hora Extra (%)', d: t.horas_extras, s: '%' },
                              { label: 'Adicional Noturno (%)', d: t.adicional_noturno, s: '%' },
                              { label: 'Jornada Semanal (h)', d: t.jornada_semanal, s: 'h' },
                              { label: 'Jornada Mensal (h)', d: t.jornada_mensal, s: 'h' },
                              { label: 'Dias Folga/Semana', d: t.dias_folga, s: '' },
                            ].map((r, i) => (
                              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                                <td className="py-2 px-2 font-medium text-slate-900">{r.label}</td>
                                <td className="py-2 px-2 text-right">{r.d?.media?.toFixed(2)}{r.s}</td>
                                <td className="py-2 px-2 text-right text-slate-500">{r.d?.p25?.toFixed(2)}{r.s}</td>
                                <td className="py-2 px-2 text-right font-medium text-blue-600">{r.d?.p50?.toFixed(2)}{r.s}</td>
                                <td className="py-2 px-2 text-right text-slate-500">{r.d?.p75?.toFixed(2)}{r.s}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            }
          </TabsContent>

          {/* ===== ANALISE TEMPORAL ===== */}
          <TabsContent value="temporal">
            {temporalLoading ? <p className="text-center text-slate-400 py-10">Carregando analise temporal...</p> :
              !temporal ? (
                <div className="bg-slate-50 rounded-xl p-10 text-center">
                  <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-600">Dados insuficientes para analise temporal.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* INPC update button + summary */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">Base: {temporal.n} empresas | {temporal.ano}</p>
                    <Button variant="outline" size="sm" onClick={handleUpdateInpc} disabled={updatingInpc} className="gap-1.5 text-xs" data-testid="update-inpc-btn">
                      <RefreshCw className={`w-3.5 h-3.5 ${updatingInpc ? 'animate-spin' : ''}`} /> {updatingInpc ? 'Atualizando INPC...' : 'Atualizar INPC (IBGE)'}
                    </Button>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat label="Total Acordos" value={temporal.resumo.total_acordos} color="blue" />
                    <Stat label="Meses com Acordos" value={temporal.resumo.meses_com_acordos} suffix="/12" />
                    <Stat label="Reajuste Medio" value={temporal.resumo.reajuste_medio_geral?.toFixed(2)} suffix="%" color="green" />
                    <Stat label="INPC Acum. Ano" value={temporal.resumo.inpc_acumulado_ano?.toFixed(2) || '-'} suffix="%" color="amber" sub="IBGE - Acumulado 12m" />
                  </div>

                  {/* Main chart: Acordos by month + Reajuste vs INPC */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Acordos por Mes de Inicio — Reajuste vs INPC ({ano})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart data={temporal.monthly_data} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Acordos', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#94a3b8' }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} label={{ value: 'Reajuste / INPC (%)', angle: 90, position: 'insideRight', fontSize: 10, fill: '#94a3b8' }} />
                          <Tooltip content={({ active, payload }) => {
                            if (!active || !payload?.[0]) return null;
                            const d = payload[0].payload;
                            return (
                              <div className="bg-white rounded-lg shadow-lg border p-3 text-xs">
                                <p className="font-semibold text-slate-900">{d.mes_label}/{ano}</p>
                                <p className="text-slate-600">{d.total_acordos} acordo(s) — {d.n_empresas} empresa(s)</p>
                                {d.reajuste_medio != null && <p className="text-blue-600">Reajuste medio: <strong>{d.reajuste_medio}%</strong></p>}
                                {d.inpc_acumulado_12m != null && <p className="text-amber-600">INPC Acum 12m: <strong>{d.inpc_acumulado_12m}%</strong></p>}
                                {d.diff_vs_inpc != null && (
                                  <p className={d.diff_vs_inpc > 0 ? 'text-emerald-600' : 'text-red-600'}>
                                    Diferenca: <strong>{d.diff_vs_inpc > 0 ? '+' : ''}{d.diff_vs_inpc}%</strong>
                                  </p>
                                )}
                              </div>
                            );
                          }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar yAxisId="left" dataKey="total_acordos" fill="#2563eb" opacity={0.3} radius={[3, 3, 0, 0]} name="Acordos no Mes" />
                          <Line yAxisId="right" type="monotone" dataKey="reajuste_medio" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} name="Reajuste Medio (%)" connectNulls />
                          <Line yAxisId="right" type="monotone" dataKey="inpc_acumulado_12m" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: '#f59e0b' }} name="INPC Acum. 12m (%)" connectNulls />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Trend Projection chart */}
                  {temporal.trend_projection?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Linha de Tendencia — Projecao 12 Meses
                        </CardTitle>
                        <p className="text-[10px] text-slate-400 mt-0.5">Regressao linear com base nos ultimos 12 meses apurados</p>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          // Combine last 12 months actual + 12 months projected
                          const actual = temporal.monthly_data.filter(m => m.reajuste_medio != null).map(m => ({
                            label: `${m.mes_label}/${ano}`,
                            reajuste_real: m.reajuste_medio,
                            inpc: m.inpc_acumulado_12m,
                            tipo: 'real',
                          }));
                          const projected = temporal.trend_projection.map(t => ({
                            label: t.mes_label,
                            reajuste_projetado: t.reajuste_projetado,
                            inpc: t.inpc_acumulado_12m,
                            tipo: 'projecao',
                          }));
                          // Bridge: last actual point also has projection
                          if (actual.length > 0 && projected.length > 0) {
                            actual[actual.length - 1].reajuste_projetado = actual[actual.length - 1].reajuste_real;
                          }
                          const combined = [...actual, ...projected];
                          return (
                            <ResponsiveContainer width="100%" height={280}>
                              <ComposedChart data={combined} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                                <Tooltip formatter={(v, n) => [`${v?.toFixed(2)}%`, n]} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="reajuste_real" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} name="Reajuste Real" connectNulls />
                                <Line type="monotone" dataKey="reajuste_projetado" stroke="#2563eb" strokeWidth={2} strokeDasharray="8 4" dot={{ r: 3, fill: '#93c5fd', stroke: '#2563eb' }} name="Tendencia Projetada" connectNulls />
                                <Line type="monotone" dataKey="inpc" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2, fill: '#f59e0b' }} name="INPC Acum. 12m" connectNulls />
                                <Area type="monotone" dataKey="reajuste_projetado" stroke="none" fill="#2563eb" fillOpacity={0.05} />
                              </ComposedChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}

                  {/* Monthly table */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Detalhamento Mensal — Reajuste vs INPC</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="temporal-table">
                          <thead>
                            <tr className="border-b-2 border-slate-200">
                              <th className="py-2 px-2 text-left font-semibold text-slate-700">Mes</th>
                              <th className="py-2 px-2 text-center font-semibold text-slate-700">Acordos</th>
                              <th className="py-2 px-2 text-center font-semibold text-slate-700">Empresas</th>
                              <th className="py-2 px-2 text-right font-semibold text-blue-700">Reajuste Medio</th>
                              <th className="py-2 px-2 text-right font-semibold text-amber-700">INPC 12m</th>
                              <th className="py-2 px-2 text-right font-semibold text-slate-700">Diferenca</th>
                            </tr>
                          </thead>
                          <tbody>
                            {temporal.monthly_data.map((m, i) => (
                              <tr key={i} className={`border-b border-slate-50 ${m.total_acordos > 0 ? 'hover:bg-blue-50/30' : 'opacity-40'}`}>
                                <td className="py-2 px-2 font-medium text-slate-900">{m.mes_label}</td>
                                <td className="py-2 px-2 text-center">{m.total_acordos}</td>
                                <td className="py-2 px-2 text-center text-slate-500">{m.n_empresas}</td>
                                <td className="py-2 px-2 text-right font-medium text-blue-700">{m.reajuste_medio != null ? `${m.reajuste_medio}%` : '-'}</td>
                                <td className="py-2 px-2 text-right text-amber-700">{m.inpc_acumulado_12m != null ? `${m.inpc_acumulado_12m}%` : '-'}</td>
                                <td className="py-2 px-2 text-right">
                                  {m.diff_vs_inpc != null ? (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${m.diff_vs_inpc > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                      {m.diff_vs_inpc > 0 ? '+' : ''}{m.diff_vs_inpc}%
                                    </span>
                                  ) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-2">Fonte INPC: IBGE — Indice Nacional de Precos ao Consumidor (acumulado 12 meses)</p>
                    </CardContent>
                  </Card>
                </div>
              )
            }
          </TabsContent>

          {/* ===== POR CATEGORIA SINDICAL ===== */}
          <TabsContent value="categorias">
            {tabLoading ? <p className="text-center text-slate-400 py-10">Carregando...</p> :
              blocked ? (
                <div className="bg-slate-50 rounded-xl p-10 text-center" data-testid="blocked-overlay-cat">
                  <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium">Governanca Estatistica</p>
                  <p className="text-xs text-slate-400 mt-1">Amostra insuficiente. Minimo: 5 empresas.</p>
                </div>
              ) : t?.por_categoria?.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">Dados abertos por categoria sindical. Nenhuma empresa informante e identificada.</p>
                  {t.por_categoria.map((cat, i) => <CategoriaBlock key={i} cat={cat} />)}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-10 text-sm">Nenhuma categoria encontrada.</p>
              )
            }
          </TabsContent>

          {/* ===== MEUS ACORDOS ===== */}
          <TabsContent value="meus">
            {myAcordos.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhum acordo registrado para {ano}.</p>
                <Button variant="outline" className="mt-3 text-xs" onClick={() => { setForm(emptyForm()); setShowForm(true); }}>Registrar Acordo</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {myAcordos.map(a => (
                  <Card key={a.id} className="hover:shadow-md transition-shadow" data-testid={`meu-acordo-${a.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900 text-sm">{a.categoria_sindical}</h4>
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">{a.tipo}</span>
                          </div>
                          <p className="text-xs text-slate-500">Vigencia: {a.vigencia_inicio} a {a.vigencia_fim}</p>
                          <div className="flex gap-3 mt-2 text-xs text-slate-600 flex-wrap">
                            <span>Reajuste: <strong>{a.percentual_reajuste}%</strong></span>
                            <span>Jornada: <strong>{a.jornada_semanal}h/sem</strong></span>
                            <span>Pisos: <strong>{a.pisos_salariais?.length || 0} faixa(s)</strong></span>
                            <span>HE: <strong>{a.horas_extras?.length || 0} faixa(s)</strong></span>
                            <span>Cesta: <strong>{a.cesta_basica ? 'Sim' : 'Nao'}</strong></span>
                            <span>Cartao: <strong>{a.cartao ? 'Sim' : 'Nao'}</strong></span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-acordo-${a.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== FORM MODAL ===== */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Registrar Acordo Coletivo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                Informe os dados do acordo coletivo vigente. Uma vez ao ano, por categoria sindical.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Categoria Sindical *</Label>
                  <Input value={form.categoria_sindical} onChange={e => onChange('categoria_sindical', e.target.value)} placeholder="Ex: Metalurgicos" className="h-9" data-testid="input-categoria" />
                </div>
                <div>
                  <Label className="text-xs">Tipo *</Label>
                  <Select value={form.tipo} onValueChange={v => onChange('tipo', v)}>
                    <SelectTrigger className="h-9" data-testid="select-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS_ACORDO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Vigencia Inicio</Label>
                  <Input type="date" value={form.vigencia_inicio} onChange={e => onChange('vigencia_inicio', e.target.value)} className="h-9" data-testid="input-vig-inicio" />
                </div>
                <div>
                  <Label className="text-xs">Vigencia Fim</Label>
                  <Input type="date" value={form.vigencia_fim} onChange={e => onChange('vigencia_fim', e.target.value)} className="h-9" data-testid="input-vig-fim" />
                </div>
                <div>
                  <Label className="text-xs">Ano Referencia</Label>
                  <Input type="number" value={form.ano_referencia} onChange={e => onChange('ano_referencia', parseInt(e.target.value) || 2024)} className="h-9" data-testid="input-ano-ref" />
                </div>
              </div>
              <div>
                <Label className="text-xs">% Reajuste</Label>
                <Input type="number" step="0.01" value={form.percentual_reajuste} onChange={e => onChange('percentual_reajuste', e.target.value)} placeholder="5.8" className="h-9 w-[140px]" data-testid="input-reajuste" />
              </div>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="pisos-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Pisos Salariais</Label>
                  <Button variant="outline" size="sm" onClick={addPiso} className="text-xs h-7 gap-1" data-testid="add-piso-btn"><Plus className="w-3 h-3" /> Faixa</Button>
                </div>
                {form.pisos_salariais.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="number" step="0.01" value={p.valor} onChange={e => updatePiso(i, 'valor', e.target.value)} placeholder="R$" className="h-8 text-sm w-[130px]" data-testid={`piso-valor-${i}`} />
                    <Input value={p.descricao} onChange={e => updatePiso(i, 'descricao', e.target.value)} placeholder="Descricao" className="h-8 text-sm flex-1" data-testid={`piso-desc-${i}`} />
                    {form.pisos_salariais.length > 1 && <button onClick={() => removePiso(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
              </div>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="he-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Horas Extras</Label>
                  <Button variant="outline" size="sm" onClick={addHE} className="text-xs h-7 gap-1" data-testid="add-he-btn"><Plus className="w-3 h-3" /> Faixa</Button>
                </div>
                {form.horas_extras.map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="number" step="0.01" value={h.percentual} onChange={e => updateHE(i, 'percentual', e.target.value)} placeholder="%" className="h-8 text-sm w-[100px]" data-testid={`he-pct-${i}`} />
                    <Input value={h.descricao} onChange={e => updateHE(i, 'descricao', e.target.value)} placeholder="Descricao" className="h-8 text-sm flex-1" data-testid={`he-desc-${i}`} />
                    {form.horas_extras.length > 1 && <button onClick={() => removeHE(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Adic. Noturno (%)</Label><Input type="number" value={form.adicional_noturno} onChange={e => onChange('adicional_noturno', e.target.value)} className="h-9" data-testid="input-adic-not" /></div>
                <div><Label className="text-xs">Jornada Semanal (h)</Label><Input type="number" value={form.jornada_semanal} onChange={e => onChange('jornada_semanal', e.target.value)} className="h-9" data-testid="input-jornada-sem" /></div>
                <div><Label className="text-xs">Jornada Mensal (h)</Label><Input type="number" value={form.jornada_mensal} onChange={e => onChange('jornada_mensal', e.target.value)} className="h-9" data-testid="input-jornada-men" /></div>
                <div><Label className="text-xs">Folga/Semana</Label><Input type="number" value={form.dias_folga_semana} onChange={e => onChange('dias_folga_semana', e.target.value)} className="h-9" data-testid="input-folga" /></div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Tipos de Escala</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ESCALAS_OPTS.map(e => (
                    <label key={e} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-colors ${form.tipos_escala.includes(e) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <Checkbox checked={form.tipos_escala.includes(e)} onCheckedChange={() => toggleEscala(e)} />{e}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-6 flex-wrap items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.cesta_basica} onCheckedChange={v => onChange('cesta_basica', v)} data-testid="check-cesta" /><span className="text-sm">Cesta Basica</span>
                </label>
                {form.cesta_basica && (
                  <div>
                    <Label className="text-[10px] text-slate-500">Valor Cesta (R$)</Label>
                    <Input type="number" step="0.01" value={form.cesta_basica_valor || ''} onChange={e => onChange('cesta_basica_valor', parseFloat(e.target.value) || 0)} placeholder="R$" className="h-8 text-sm w-[120px]" data-testid="input-cesta-valor" />
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.cartao} onCheckedChange={v => onChange('cartao', v)} data-testid="check-cartao" /><span className="text-sm">Cartao</span>
                </label>
                {form.cartao && (
                  <div>
                    <Label className="text-[10px] text-slate-500">Valor Cartao (R$)</Label>
                    <Input type="number" step="0.01" value={form.cartao_valor || ''} onChange={e => onChange('cartao_valor', parseFloat(e.target.value) || 0)} placeholder="R$" className="h-8 text-sm w-[120px]" data-testid="input-cartao-valor" />
                  </div>
                )}
              </div>
              <Button onClick={handleSubmit} className="w-full gap-2" data-testid="submit-acordo-btn"><Save className="w-4 h-4" /> Registrar Acordo Coletivo</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default AcordosColetivos;
